<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import type { Address } from "@types"
import { computed, inject, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { SIDEBAR_CONTEXT_KEY } from "@core/layouts/sidebar-context"
import type { SidebarMenuItem } from "@core/layouts/sidebars/sidebar-menu.types"
import { storeToRefs } from "pinia"
import { useAuthStore } from "@core/stores/auth.store"
import { useInterrupteursStore } from "@core/stores/interrupteurs.store"
import {
    entreesMenuOptimTurpe,
    libelleGroupeCourtage,
} from "@modules/courtage/utils/optimturpe-v2.acces"
import { useBuildingStore } from "@core/stores/building-v2.store"
import {
    BUILDING_INFO_GENERAL_SECTION_IDS,
    useBuildingInfoGeneralSectionVisibility,
} from "@core/composables/useBuildingInfoGeneralSections"
import { useResponsive } from "@core/composables"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            menu: {
                infoGeneral: "Informations générales",
                sourcesData: "Sources de données",
                compteur: "Compteurs",
                data: "Données",
                capteurs: "Capteurs",
                weatherStation: "Station météo",
                share: "Partage d'accès",
                chauffage: "Chauffage",
                chauffageSetting: "Réglage de la chaufferie",
                chauffageData: "Conso. chauffage",
                chauffageTemp: "Analyse température ambiante",
                energyManager: "Energy Manager",
                profilUtilisation: "Profil d'utilisation",
                evolutionConso: "Évolution de la conso.",
                irve: "Analyse I.R.V.E",
                pv: "Dimensionnement P.V",
                pvConso: "Conso. et extrapolation",
                pvEtude: "Étude photovoltaïque",
                pvEtudeEco: "Étude économique",
                pvHistory: "Historique des études",
                courtage: "Optimisation Contractuelle",
                optimTurpe: "Optimisation du Turpe",
                comparateurOffres: "Comparateur d'offres",
                alarm: "Alarmes",
                infoGeneralSections: {
                    address: "Identité du bâtiment",
                    relation: "Relations entre bâtiments",
                    building: "Informations bâtiment",
                    house: "Informations logement/local",
                    usageProfile: "Profil d'utilisation",
                    actors: "Acteurs du bâtiment",
                },
                lockHintChauffageData:
                    "Souscrivez au module Exploitant chauffagiste pour accéder à cette fonctionnalité.",
                lockHintChauffageSetting:
                    "Souscrivez au module Exploitant chauffagiste pour accéder à cette fonctionnalité.",
                lockHintChauffageTemp:
                    "Souscrivez au module Exploitant chauffagiste pour accéder à cette fonctionnalité.",
                lockHintViewOnlyBuilding:
                    "Ce bâtiment vous a été partagé en lecture seule : cette section n'est pas accessible.",
                // Session armée pour la V2 : les noms du module viennent de
                // `NOMS_OPTIMTURPE_V2_OUVERTE` (D24, maquette mq-menu variante A).
            },
            header: {
                share: "Partager",
                remove: "Supprimer",
                folder: "Retour au dossier",
                folderTooltip: "Retour au dossier",
                views: "Partagé",
                sharedBuildingHint:
                    "Ce bâtiment vous a été partagé : certaines fonctionnalités ne sont pas accessibles.",
            },
        },
    },
})

// ================================
// ÉTAT
// ================================
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const interrupteurs = useInterrupteursStore()
const buildingStore = useBuildingStore()
const homeWorkspace = useHomeWorkspaceStore()
const { getBuildingIsViewOnly, getBuildingHasOwns } = storeToRefs(buildingStore)
const buildingId = computed(() => route.params._id_building as string)

const { getShowRelation, getIsHousingCardDisplay, getShowBuildingBlock } =
    useBuildingInfoGeneralSectionVisibility()
const ownerMenuRef = ref<{ toggle: (e: Event) => void } | null>(null)
const { isMobile } = useResponsive()
const sidebarContext = inject(SIDEBAR_CONTEXT_KEY)
const collapsed = computed(() => sidebarContext?.collapsed?.value ?? false)

/** Referme la sidebar sur mobile après navigation depuis l’en-tête. */
function onCollapseSidebarIfMobile() {
    if (!isMobile.value) return
    sidebarContext?.collapse?.()
}

// ================================
// HELPERS
// ================================
function formatAddress(address: Address | undefined): string {
    if (!address) return ""
    if (address.inline_name) return address.inline_name
    const parts = [
        address.street,
        [address.zipcode, address.city].filter(Boolean).join(" "),
    ].filter(Boolean)
    return parts.join(", ")
}

function getBuildingIconType(niveau: string | undefined): string {
    if (!niveau) return "default"
    const n = niveau.toLowerCase()
    if (n.includes("groupe")) return "groupe"
    if (n.includes("appartement")) return "appartement"
    if (n.includes("maison")) return "maison"
    return "default"
}

// ================================
// COMPUTED
// ================================
const isBuildingLoading = computed(() =>
    Boolean(
        buildingId.value &&
            buildingStore.isFetchingBuilding &&
            buildingId.value !== buildingStore.building?._id_building
    )
)
const building = computed(() => buildingStore.building)
const buildingName = computed(() => building.value?.name ?? "-")
const buildingAddress = computed(() => formatAddress(building.value?.address))
const buildingNiveau = computed(() => building.value?.infos?.niveau)
/** Menu en-tête (partage / suppression) : visible pour les propriétaires et en lecture seule. */
const showBuildingOwnerMenu = computed(
    () => getBuildingHasOwns.value || getBuildingIsViewOnly.value
)
const folderPath = computed(() => {
    const folder = building.value?.folder
    return folder?._id_folder ? `/folder/${folder._id_folder}` : null
})

/**
 * Suppression d'un bâtiment réservée aux admin/owner propriétaires (table des
 * droits DEV-16282 : Membre et Utilisateur ne peuvent pas supprimer). Le menu
 * en-tête reste visible (partage) pour les propriétaires et en lecture seule.
 */
const getCanDeleteBuilding = computed(
    () => authStore.isCompanyAdmin && getBuildingHasOwns.value
)

const ownerMenuModel = computed(() => {
    const id = buildingId.value
    const items = [
        {
            label: t("header.share"),
            icon: "pi pi-users",
            command: () => {
                if (id) {
                    router.push(`/building/${id}/partage`)
                    onCollapseSidebarIfMobile()
                }
            },
        },
    ]
    if (getCanDeleteBuilding.value) {
        items.push({
            label: t("header.remove"),
            icon: "pi pi-trash",
            command: () => {
                if (!id) return
                homeWorkspace.openModal("buildingRemove", {
                    buildingId: id,
                })
                onCollapseSidebarIfMobile()
            },
        })
    }
    return items
})

function toggleOwnerMenu(event: Event) {
    ownerMenuRef.value?.toggle(event)
}

// ================================
// CONFIG MENU BÂTIMENT (aligné sur legacy pages/building/_id.vue)
// ================================
const menuModel = computed<SidebarMenuItem[]>(() => {
    const id = buildingId.value
    if (!id) return []
    const viewOnly = getBuildingIsViewOnly.value
    const lockViewOnly = () => t("menu.lockHintViewOnlyBuilding")
    const base = `/building/${id}`
    const infoGeneralPath = `${base}/info-general`
    const sid = BUILDING_INFO_GENERAL_SECTION_IDS

    const infoGeneralSubItems: SidebarMenuItem[] = [
        {
            key: "info-general-address",
            label: t("menu.infoGeneralSections.address"),
            route: { path: infoGeneralPath, hash: `#${sid.address}` },
        },
    ]
    if (getShowRelation.value) {
        infoGeneralSubItems.push({
            key: "info-general-relation",
            label: t("menu.infoGeneralSections.relation"),
            route: { path: infoGeneralPath, hash: `#${sid.relation}` },
        })
    }
    if (getShowBuildingBlock.value) {
        infoGeneralSubItems.push({
            key: "info-general-building",
            label: t("menu.infoGeneralSections.building"),
            route: { path: infoGeneralPath, hash: `#${sid.building}` },
        })
    }
    if (getIsHousingCardDisplay.value) {
        infoGeneralSubItems.push({
            key: "info-general-house",
            label: t("menu.infoGeneralSections.house"),
            route: { path: infoGeneralPath, hash: `#${sid.house}` },
        })
    }
    if (authStore.hasOfferEnergyManager) {
        infoGeneralSubItems.push({
            key: "info-general-usage-profile",
            label: t("menu.infoGeneralSections.usageProfile"),
            route: { path: infoGeneralPath, hash: `#${sid.usageProfile}` },
        })
    }
    infoGeneralSubItems.push(
        {
            key: "info-general-actors",
            label: t("menu.infoGeneralSections.actors"),
            route: { path: infoGeneralPath, hash: `#${sid.actors}` },
        },
        {
            key: "info-general-partage",
            label: t("menu.share"),
            route: { path: `${base}/partage` },
        }
    )

    const items: SidebarMenuItem[] = [
        {
            key: "info-general",
            label: t("menu.infoGeneral"),
            icon: "pi pi-info-circle",
            route: { path: infoGeneralPath },
            items: infoGeneralSubItems,
        },
        {
            key: "sources-data",
            label: t("menu.sourcesData"),
            icon: "pi pi-sitemap",
            items: [
                {
                    key: "compteur",
                    label: t("menu.compteur"),
                    icon: "pi pi-bolt",
                    route: { path: `${base}/compteur` },
                },
                {
                    key: "objet-connecte",
                    label: t("menu.capteurs"),
                    icon: "fa-light fa-sensor",
                    route: { path: `${base}/objet-connecte` },
                },
                {
                    key: "station-meteo",
                    label: t("menu.weatherStation"),
                    icon: "fa-light fa-cloud-sun",
                    route: { path: `${base}/station-meteo` },
                },
            ],
        },
        {
            key: "data",
            label: t("menu.data"),
            icon: "pi pi-chart-line",
            route: { path: `${base}/data` },
        },
    ]

    if (authStore.hasOfferIrve) {
        items.push({
            key: "irve",
            label: t("menu.irve"),
            icon: "pi pi-car",
            route: { path: `${base}/irve/simulation` },
            disabled: viewOnly,
            lockMessage: viewOnly ? lockViewOnly() : undefined,
        })
    }

    if (authStore.hasOfferPV) {
        items.push({
            key: "pv",
            label: t("menu.pv"),
            icon: "pi pi-sun",
            items: [
                {
                    key: "consommation-pv",
                    label: t("menu.pvConso"),
                    route: { path: `${base}/pv/consommation-pv` },
                    disabled: viewOnly,
                    lockMessage: viewOnly ? lockViewOnly() : undefined,
                },
                {
                    key: "pv-etude",
                    label: t("menu.pvEtude"),
                    route: { path: `${base}/pv/etude-pv` },
                    disabled: viewOnly,
                    lockMessage: viewOnly ? lockViewOnly() : undefined,
                },
                {
                    key: "etude-economique-pv",
                    label: t("menu.pvEtudeEco"),
                    route: { path: `${base}/pv/etude-economique-pv` },
                    disabled: viewOnly,
                    lockMessage: viewOnly ? lockViewOnly() : undefined,
                },
                {
                    key: "history-pv",
                    label: t("menu.pvHistory"),
                    route: { path: `${base}/pv/history-pv` },
                    disabled: viewOnly,
                    lockMessage: viewOnly ? lockViewOnly() : undefined,
                },
            ],
        })
    }

    /**
     * Bloc courtage : aligné sur les tags Stripe distincts — `optim-turpe` pour Optim Turpe
     * (cf. route `hasModuleOptimTurpe`), `courtage` pour le comparateur (`hasOfferCourtage`).
     * Sinon un compte avec uniquement `optim-turpe` ne voyait pas le lien alors que la page était autorisée.
     */
    const courtageItems: SidebarMenuItem[] = []

    // Décision A13 du 21/09/2026 : les deux versions sont gardées en parallèle
    // et le MENU les distingue. Le support de la V1 s'arrête quinze jours après
    // la release de la V2, sa fin de vie est le 31/12/2026 ; les deux dates sont
    // écrites dans la page V1 elle-même. Gating identique pour les deux
    // entrées (même module payant, même rôle), et l'entrée V2 attend en plus
    // l'interrupteur de runtime `OPTIMTURPE_V2` (DEV-1892) : baissé, le menu
    // est celui de la V1 seule.
    courtageItems.push(
        ...entreesMenuOptimTurpe(
            {
                hasModuleOptimTurpe: authStore.hasModuleOptimTurpe,
                isEmployeWriteOrHigher: authStore.isEmployeWriteOrHigher,
                optimturpeV2Arme: interrupteurs.optimturpeV2Arme,
            },
            base,
            { v1: t("menu.optimTurpe") }
        )
    )

    if (authStore.hasOfferCourtage) {
        courtageItems.push({
            key: "comparateur-offres",
            label: t("menu.comparateurOffres"),
            route: { path: `${base}/courtage/comparateur-offres` },
        })
    }

    if (courtageItems.length > 0) {
        items.push({
            key: "courtage",
            label: libelleGroupeCourtage(
                interrupteurs.optimturpeV2Arme,
                t("menu.courtage")
            ),
            icon: "pi pi-wallet",
            items: courtageItems,
        })
    }

    if (authStore.hasOfferEnergyManager) {
        items.push({
            key: "energy-manager",
            label: t("menu.energyManager"),
            icon: "fa-light fa-chart-network",
            items: [
                {
                    key: "profil-utilisation",
                    label: t("menu.profilUtilisation"),
                    route: {
                        path: `${base}/energy-manager/profil-utilisation`,
                    },
                    disabled: viewOnly,
                    lockMessage: viewOnly ? lockViewOnly() : undefined,
                },
                {
                    key: "evolution-conso",
                    label: t("menu.evolutionConso"),
                    route: {
                        path: `${base}/energy-manager/evolution-conso`,
                    },
                    disabled: viewOnly,
                    lockMessage: viewOnly ? lockViewOnly() : undefined,
                },
                {
                    key: "alarmes",
                    label: t("menu.alarm"),
                    route: {
                        path: `${base}/alarme`,
                    },
                    disabled: viewOnly,
                    lockMessage: viewOnly ? lockViewOnly() : undefined,
                },
            ],
        })
    }

    if (authStore.hasOfferEnergyManager) {
        items.push({
            key: "chauffage",
            label: t("menu.chauffage"),
            icon: "fa-light fa-fire",
            items: [
                {
                    key: "consommation-chauffage",
                    label: t("menu.chauffageData"),
                    route: {
                        path: `${base}/chauffage/consommation`,
                    },
                    disabled:
                        !authStore.hasModuleExploitantChauffagiste || viewOnly,
                    lockMessage: viewOnly
                        ? lockViewOnly()
                        : t("menu.lockHintChauffageData"),
                },
                {
                    key: "reglage-chaufferie",
                    label: t("menu.chauffageSetting"),
                    route: {
                        path: `${base}/chauffage/reglage-chaufferie`,
                    },
                    disabled:
                        !authStore.hasModuleExploitantChauffagiste || viewOnly,
                    lockMessage: viewOnly
                        ? lockViewOnly()
                        : t("menu.lockHintChauffageSetting"),
                },
                {
                    key: "analyse-temp-ambiance",
                    label: t("menu.chauffageTemp"),
                    route: {
                        path: `${base}/chauffage/analyse-temp-ambiance`,
                    },
                    disabled:
                        !authStore.hasModuleExploitantChauffagiste || viewOnly,
                    lockMessage: viewOnly
                        ? lockViewOnly()
                        : t("menu.lockHintChauffageTemp"),
                },
            ],
        })
    }

    return items
})
</script>

<template>
    <BaseSidebar :model="menuModel" :loading="isBuildingLoading">
        <template #top>
            <!-- Chargement : skeletons infos bâtiment -->
            <div
                v-if="isBuildingLoading"
                class="flex flex-col gap-3 px-2 pb-2 mb-2 border-b border-surface-200"
                data-sidebar-toggle
            >
                <div class="flex items-center gap-2">
                    <BaseSkeleton
                        shape="circle"
                        size="2.5rem"
                        class="shrink-0"
                    />
                    <BaseSkeleton width="60%" height="1.25rem" />
                </div>
                <BaseSkeleton width="100%" height="1rem" />
                <BaseSkeleton width="80%" height="1rem" />
            </div>
            <!-- Mode replié : icône bâtiment + lien dossier + menu ... -->
            <div
                v-else-if="building && collapsed"
                class="flex flex-col items-center gap-2 px-2 pb-2 mb-2 border-b border-surface-200"
                data-sidebar-toggle
            >
                <BaseBuildingIcon
                    :type="getBuildingIconType(buildingNiveau)"
                    :size="28"
                    class="shrink-0"
                />
                <BaseButton
                    v-if="folderPath"
                    v-tooltip.right="t('header.folderTooltip')"
                    icon="pi pi-folder-open"
                    link
                    rounded
                    class="h-10 w-10 min-w-10 shrink-0 p-0"
                    :aria-label="t('header.folderTooltip')"
                    @click="
                        () => {
                            if (folderPath) {
                                router.push(folderPath)
                                onCollapseSidebarIfMobile()
                            }
                        }
                    "
                />
                <BaseButton
                    v-if="showBuildingOwnerMenu"
                    icon="pi pi-ellipsis-v"
                    text
                    rounded
                    severity="secondary"
                    class="h-10 w-10 min-w-10 shrink-0 p-0"
                    aria-label="Menu bâtiment"
                    @click="toggleOwnerMenu"
                />
            </div>
            <!-- Mode étendu : nom, adresse, menu, lien dossier -->
            <div
                v-else-if="building && !collapsed"
                class="flex flex-col gap-3 px-2 pb-2 mb-2 border-b border-surface-200"
                data-sidebar-toggle
            >
                <!-- Ligne titre + menu -->
                <div class="flex items-start justify-between gap-2 min-w-0">
                    <div class="flex items-center gap-2 min-w-0">
                        <BaseBuildingIcon
                            :type="getBuildingIconType(buildingNiveau)"
                            :size="40"
                            class="shrink-0"
                        />
                        <div class="flex flex-col min-w-0">
                            <h2
                                class="text-base font-semibold text-surface-900"
                                :title="buildingName"
                            >
                                {{ buildingName }}
                            </h2>
                            <div
                                v-if="getBuildingIsViewOnly"
                                class="flex items-center gap-1 min-w-0"
                            >
                                <span class="text-xs italic text-surface-600">
                                    {{ t("header.views") }}
                                </span>
                                <BaseTooltipMessage
                                    severity="secondary"
                                    position="bottom"
                                    size="extra-small"
                                    :content="t('header.sharedBuildingHint')"
                                />
                            </div>
                        </div>
                    </div>
                    <BaseButton
                        v-if="showBuildingOwnerMenu"
                        icon="pi pi-ellipsis-v"
                        text
                        rounded
                        size="small"
                        severity="secondary"
                        class="shrink-0"
                        @click="toggleOwnerMenu"
                    />
                </div>
                <!-- Adresse -->
                <p
                    v-if="buildingAddress"
                    class="text-sm text-surface-700 line-clamp-2 px-2"
                    :title="buildingAddress"
                >
                    {{ buildingAddress }}
                </p>
                <!-- Lien dossier -->
                <BaseButton
                    v-if="folderPath"
                    :label="t('header.folder')"
                    link
                    size="small"
                    icon="pi pi-chevron-left"
                    icon-pos="left"
                    class="p-0 justify-start text-sm w-fit"
                    @click="
                        () => {
                            if (folderPath) {
                                router.push(folderPath)
                                onCollapseSidebarIfMobile()
                            }
                        }
                    "
                />
            </div>
            <!-- Menu ... (partage / suppression), rendu pour les deux modes -->
            <BaseMenu
                v-if="building && showBuildingOwnerMenu"
                ref="ownerMenuRef"
                :model="ownerMenuModel"
                popup
            />
        </template>
    </BaseSidebar>
</template>
